import { RoadieApiError, RoadieApiResponse } from 'roadie-ui'
import {
  DeliveryCertificationDetails,
  DeliveryCertificationDetailsApiResponse,
  DriverAction,
  DriverActionApiResponse,
  EventApiResponse,
  GigDetails,
  GigDetailsApiResponse,
  GigEvent,
  GigLocation,
  Image,
  ImageApiResponse,
  Item,
  ItemApiResponse,
  LocationApiResponse,
  Pricing,
  PricingApiResponse,
  Sender,
  SenderApiResponse,
  SenderSettings,
  SenderSettingsApiResponse,
} from 'types/gigDetails'

export function deserializeGigDetails(apiResponse: RoadieApiResponse | RoadieApiError): GigDetails[] {
  if (('isRoadieApiError' in apiResponse && apiResponse.isRoadieApiError) || !('data' in apiResponse)) {
    return []
  }

  const gigDetailsData: GigDetailsApiResponse[] = apiResponse.data.gigs
  if (!gigDetailsData) {
    return []
  }

  const details: GigDetails[] = []
  for (const detail of gigDetailsData) {
    details.push({
      alternateId1: detail.alternate_id_1 || null,
      automatedPickupGig: detail.automated_pickup_gig,
      automatedReturnGig: detail.automated_return_gig,
      bag: detail.bag,
      cancellationSourceApplication: detail.cancellation_source_application ?? null,
      category: detail.category,
      checklistRequired: detail.checklist_required,
      consolidationId: detail.consolidation_id || null,
      createdAt: detail.created_at,
      creationContext: detail.creation_context,
      creationSourceApplication: detail.creation_source_application,
      deliveryCertifications: detail.delivery_certifications,
      deliveryCertificationDetails: deserializeDeliveryCertificationDetails(detail.delivery_certification_details),
      deliveryConfirmationRequired: detail.delivery_confirmation_required,
      deliveryDeadline: detail.delivery_deadline,
      deliveryLocation: detail.delivery_location ? deserializeLocation(detail.delivery_location) : null,
      deliveryOptions: detail.delivery_options,
      deliveryPhotoRequired: detail.delivery_photo_required,
      deliveryWindow: detail.delivery_window || null,
      description: detail.description,
      driverActions: detail.driver_actions ? deserializeDriverActions(detail.driver_actions) : [],
      events: deserializeGigEvents(detail.events),
      id: detail.id,
      images: deserializeImages(detail.images),
      items: detail.items ? deserializeItems(detail.items) : [],
      optionalScans: detail.optional_scans,
      parentGigId: detail.parent_gig_id,
      pickupLocation: detail.pickup_location ? deserializeLocation(detail.pickup_location) : null,
      pickupPhotoRequired: detail.pickup_photo_required,
      pickupTime: detail.pickup_time,
      pricing: deserializePricing(detail.pricing),
      recipientAgeVerificationRequired: detail.recipient_age_verification_required,
      recipientIdScanRequired: detail.recipient_id_scan_required,
      recipientSignature: detail.recipient_signature,
      referenceId: detail.reference_id || null,
      requiredDeliveryScans: detail.required_delivery_scans,
      requiredPickupScans: detail.required_pickup_scans,
      route: detail.route,
      sender: deserializeSender(detail.sender),
      senderSettings: deserializeSenderSettings(detail.sender_settings),
      shipmentType: detail.shipment_type,
      signatureImageUrl: detail.signature_image_url || null,
      size: detail.size,
      status: detail.status,
      supportId: detail.support_id,
      title: detail.title,
      totalDistance: detail.total_distance || null,
      updatedAt: detail.updated_at,
    })
  }

  return details
}

function deserializePricing(pricing: PricingApiResponse): Pricing {
  return {
    amountPaidToRoadie: pricing.amount_paid_to_roadie,
    bookingFee: pricing.booking_fee,
    insuranceFee: pricing.insurance_fee,
    totalPrice: pricing.total_price,
    lineItems: pricing.line_items || [],
  }
}

function deserializeDeliveryCertificationDetails(
  certDetails: DeliveryCertificationDetailsApiResponse[]
): DeliveryCertificationDetails[] {
  if (!certDetails) {
    return []
  }

  const certificationDetails: DeliveryCertificationDetails[] = []
  for (const certDetail of certDetails) {
    certificationDetails.push({
      name: certDetail.name,
      description: certDetail.description,
      friendlyLabel: certDetail.friendly_label,
    })
  }

  return certificationDetails
}

function deserializeSender(sender: SenderApiResponse): Sender {
  return {
    id: sender.id || 0,
    masterAccountId: sender.master_account_id || null,
    preferredName: sender.preferred_name || '',
    averageRating: sender.average_rating || 0,
    ratingCount: sender.rating_count || 0,
    avatarUrl: sender.avatar_url || '',
    verifiedAvatarUrl: sender.verified_avatar_url || '',
    expensesEnabled: sender.expenses_enabled || false,
  }
}

function deserializeLocation(location: LocationApiResponse): GigLocation {
  return {
    metadata: {
      coordinatesSource: location.metadata?.coordinates_source || '',
    },
    storeNumber: location.store_number || '',
    enforceGeofence: location.enforce_geofence || false,
    latitude: location.latitude || 0,
    longitude: location.longitude || 0,
    city: location.city || '',
    state: location.state || '',
    zip: location.zip || '',
  }
}

function deserializeImages(images: ImageApiResponse[]): Image[] {
  if (!images) {
    return []
  }

  const gigImages: Image[] = []
  for (const image of images) {
    gigImages.push({
      id: image.id,
      type: image.type,
      index: image.index,
      filename: image.filename,
      imageLocation: image.image_location,
      contentType: image.content_type,
      url: image.url,
    })
  }

  return gigImages
}

function deserializeItems(items: ItemApiResponse[]): Item[] {
  if (!items) {
    return []
  }

  const gigItems: Item[] = []
  for (const item of items) {
    gigItems.push({
      id: item.id,
      length: item.length,
      width: item.width,
      height: item.height,
      weight: item.weight,
      quantity: item.quantity,
      pickedUpQuantity: item.picked_up_quantity,
    })
  }

  return gigItems
}

function deserializeDriverActions(actions: DriverActionApiResponse[]): DriverAction[] {
  if (!actions) {
    return []
  }

  const driverActions: DriverAction[] = []
  for (const action of actions) {
    driverActions.push({
      id: action.id,
      masterAccountId: action.master_account_id || null,
      triggerType: action.trigger_type,
      triggerValue: action.trigger_value,
      description: action.description,
      required: action.required || false,
      actionId: action.action_id,
      actionType: action.action_type,
      actionAttributes: action.action_attributes,
      createdAt: action.created_at,
      updatedAt: action.updated_at,
      title: action.title,
    })
  }

  return driverActions
}

function deserializeSenderSettings(settings: SenderSettingsApiResponse): SenderSettings {
  return {
    allowPartialBarcodeMatch: settings.allow_partial_barcode_match,
    secureHandoff: settings.secure_handoff,
    returnSenderUps: settings.return_sender_ups,
    premierSupport: settings.premier_support || false,
    customPickupWaitTimeMinutes: settings.customer_pickup_wait_time_minutes || 0,
    customDeliveryWaitTimeMinutes: settings.custom_delivery_wait_time_minutes || 0,
    latestDeliveryTime: settings.latest_delivery_time,
    skipIdVerification: settings.skip_id_verification,
    secureReturnHandoff: settings.secure_return_handoff,
  }
}

function deserializeGigEvents(events: EventApiResponse[]): GigEvent[] {
  if (!events) {
    return []
  }

  const gigEvents: GigEvent[] = []
  for (const event of events) {
    gigEvents.push({
      eventType: event.event_type,
      createdAt: event.created_at,
      location: event.location || null,
    })
  }

  return gigEvents
}
